/**
 * User Role Configuration
 * Maps numeric role IDs from backend to role names
 */

export const ROLES = {
  ADMIN: "1",
  MANAGER: "2",
  EDITOR: "3",
  AUTHOR: "4",
  CONTRIBUTOR: "5",
  MEMBER: "6",
  ACCREDITOR: "7",
  NO_ACCESS: "8",
};

export const ROLE_NAMES = {
  1: "admin",
  2: "manager",
  3: "editor",
  4: "author",
  5: "contributor",
  6: "member",
  7: "accreditor",
  8: "noaccess",
};

export const ROLE_LABELS = {
  1: "Administrator",
  2: "Manager",
  3: "Editor",
  4: "Author",
  5: "Contributor",
  6: "Member",
  7: "Accreditor",
  8: "No Access",
};

/**
 * Check if user has admin access (role 1 or 2)
 */
export const isAdmin = (roleId) => {
  return roleId === "1" || roleId === "2";
};

/**
 * Check if user has editor access (role 1, 2, or 3)
 */
export const isEditor = (roleId) => {
  return ["1", "2", "3"].includes(roleId);
};

/**
 * Check if user has author access (role 1-4)
 */
export const isAuthor = (roleId) => {
  return ["1", "2", "3", "4"].includes(roleId);
};

/**
 * Check if user can contribute (role 1-5)
 */
export const canContribute = (roleId) => {
  return ["1", "2", "3", "4", "5"].includes(roleId);
};

/**
 * Check if user has any access (not role 8).
 * Accreditor (7) has access, but only to the accreditation overview — the
 * tier checks above are explicit allowlists of 1-5, so it correctly falls
 * out of all of them.
 */
export const hasAccess = (roleId) => {
  return roleId !== "8";
};

/**
 * Check if user is an accreditor (role 7) — scoped to the accreditation
 * overview only, see hasAccess() above.
 */
export const isAccreditor = (roleId) => {
  return roleId === ROLES.ACCREDITOR;
};

/**
 * Check if user can access the accreditation overview and its detail pages
 * — admins, managers (isAdmin covers both role 1 and 2), and accreditors.
 * Everyone else (editor/author/contributor/member) is turned away, even
 * with a known URL — see the /accreditation guard in ProtectedRoute.
 */
export const canAccessAccreditation = (roleId) => {
  return isAdmin(roleId) || isAccreditor(roleId);
};

/**
 * Default landing route right after sign-in, based on role. Accreditors
 * land on the accreditation overview instead of the regular client home.
 */
export const getDefaultLandingPath = (roleId) => {
  return isAccreditor(roleId) ? "/accreditation" : "/home";
};

/**
 * Resolve where to send a user after sign-in (or when bouncing an
 * already-authenticated user off an auth page). Honors a requested
 * `fromPathname` (e.g. a deep link they hit before being sent to login),
 * except for an accreditor requesting somewhere outside /accreditation —
 * that's out of their scope, so they fall back to the default landing path.
 */
export const resolveLandingPath = (roleId, fromPathname) => {
  const defaultPath = getDefaultLandingPath(roleId);
  if (!fromPathname) return defaultPath;
  if (isAccreditor(roleId) && !fromPathname.startsWith("/accreditation")) {
    return defaultPath;
  }
  return fromPathname;
};

// Role IDs rank inversely: 1 (Administrator) outranks 8 (No Access). A
// missing/unparseable value yields null so the callers below can fail open.
const toRank = (roleId) => {
  if (roleId === null || roleId === undefined || roleId === "") return null;
  const rank = Number(roleId);
  return Number.isFinite(rank) && rank > 0 ? rank : null;
};

/**
 * Can a user holding `ownRoleId` create, update or delete a user holding
 * `targetRole`? Mirrors the API rule: you may not manage a user with a HIGHER
 * role than your own; an equal role is fine. Unknown values on either side
 * fail open — the API still enforces the rule, this only decides whether the
 * UI offers the action at all.
 */
export const canManageRole = (ownRoleId, targetRole) => {
  const own = toRank(ownRoleId);
  const target = toRank(targetRole);
  if (own === null || target === null) return true;
  return target >= own;
};

/**
 * Filter `/users/info`-style role options ({ key, value }) down to the roles
 * a user holding `ownRoleId` is allowed to assign — their own role and below.
 */
export const getAssignableRoles = (ownRoleId, roles) => {
  return (roles || []).filter((role) => canManageRole(ownRoleId, role?.key ?? role?.value));
};

/**
 * Get role name from numeric ID
 */
export const getRoleName = (roleId) => {
  return ROLE_NAMES[roleId] || "unknown";
};

/**
 * Get role label from numeric ID
 */
export const getRoleLabel = (roleId) => {
  return ROLE_LABELS[roleId] || "Unknown";
};

export default {
  ROLES,
  ROLE_NAMES,
  ROLE_LABELS,
  isAdmin,
  isEditor,
  isAuthor,
  canContribute,
  hasAccess,
  isAccreditor,
  canAccessAccreditation,
  getDefaultLandingPath,
  resolveLandingPath,
  canManageRole,
  getAssignableRoles,
  getRoleName,
  getRoleLabel,
};
